import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

function Privacy() {
  return (
    <div className="min-h-screen bg-white font-quicksand">
      <SEO
        title="Privacy Policy"
        description="Privacy policy for StudyPass RDH, the NBDHE dental hygiene board exam prep platform."
        path="/privacy"
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-12 px-6 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-study-black mb-2">
          Privacy Policy
        </h1>
        <p className="text-study-black">Last updated: January 2025</p>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          <p className="text-study-black text-lg mb-6">
            Study Pass LLC ("StudyPassRDH," "we," "our," "us") respects the privacy of individuals
            who access our website ("https://www.studypassrdh.com") or use our services. This
            privacy policy ("Policy") outlines how we collect, use, disclose, and safeguard your
            information. By accessing our website or utilizing our services, you agree to the
            practices described in this Policy.
          </p>

          <p className="text-study-black mb-8">
            Should there be future updates to our Policy, those changes will be posted on this
            page with an updated revision date. Your continued use of our website and services
            constitutes your acknowledgment of the updated terms.
          </p>

          {/* Section 1 */}
          <Section number="1" title="Information We Collect">
            <p className="text-study-black mb-4">
              We collect information when you access our website, create an account, or utilize
              our services (including web and mobile applications). Information is collected in
              the following categories:
            </p>

            <ul className="list-disc pl-6 mb-6 space-y-2 text-study-black">
              <li>
                <strong>Identifiers:</strong> Such as your name, email address, phone number,
                IP address, and account information.
              </li>
              <li>
                <strong>Personal Information:</strong> Including educational background, payment
                information, and mailing address.
              </li>
              <li>
                <strong>Professional or Educational Data:</strong> Such as details about your
                dental hygiene program, school attended, and date of graduation.
              </li>
              <li>
                <strong>Commercial Data:</strong> Information about purchases, subscriptions,
                or service interactions.
              </li>
              <li>
                <strong>Internet Activity:</strong> Browsing history, search history, interaction
                with our services, and cookies.
              </li>
              <li>
                <strong>Geolocation Data:</strong> Physical location data based on your IP address.
              </li>
            </ul>

            <p className="text-study-black mb-4">
              We collect this information in the following ways:
            </p>

            <ul className="list-disc pl-6 mb-4 space-y-2 text-study-black">
              <li>
                <strong>Information You Provide:</strong> This includes data submitted through
                account creation, subscription processes, or contact forms.
              </li>
              <li>
                <strong>Information Collected Automatically:</strong> For example, IP address,
                device details, browser type, access time, and pages visited via cookies and
                similar tracking tools.
              </li>
            </ul>
          </Section>

          {/* Section 2 */}
          <Section number="2" title="How We Use Collected Information">
            <p className="text-study-black mb-4">
              We use the information we collect for the following purposes:
            </p>

            <ul className="list-disc pl-6 mb-4 space-y-2 text-study-black">
              <li>To provide and improve our services.</li>
              <li>To process transactions and manage your account.</li>
              <li>To personalize your experience on our website.</li>
              <li>To communicate updates, promotions, and offers (where consented).</li>
              <li>To prevent fraud, address technical issues, and ensure platform security.</li>
              <li>To analyze trends and improve website usability and offerings.</li>
              <li>To comply with legal requirements and enforce our Terms of Use.</li>
            </ul>
          </Section>

          {/* Section 3 */}
          <Section number="3" title="Information Sharing and Disclosure">
            <p className="text-study-black mb-4">
              We may share information with:
            </p>

            <ul className="list-disc pl-6 mb-6 space-y-2 text-study-black">
              <li>
                <strong>Authorized Personnel:</strong> Employees or contractors responsible for
                supporting our operations.
              </li>
              <li>
                <strong>Third-Party Providers:</strong> Partners who assist in payment processing,
                data storage, or analytics services.
              </li>
              <li>
                <strong>Legal Compliance:</strong> Authorities when required by law, legal
                proceedings, or regulations.
              </li>
              <li>
                <strong>Business Transfers:</strong> Successor entities in case of mergers,
                acquisitions, or asset transfers.
              </li>
            </ul>

            <p className="text-study-black font-semibold">
              We do not sell your personal information to third parties without your explicit consent.
            </p>
          </Section>

          {/* Section 4 */}
          <Section number="4" title="Cookies and Tracking Technologies">
            <p className="text-study-black mb-4">
              We use cookies to enhance your browsing experience. These small files allow us to:
            </p>

            <ul className="list-disc pl-6 mb-4 space-y-2 text-study-black">
              <li>Store preferences and session details.</li>
              <li>Track usage and website performance.</li>
              <li>Tailor content and advertisements.</li>
            </ul>

            <p className="text-study-black">
              You can modify your browser settings to disable cookies; however, some features
              may become unavailable.
            </p>
          </Section>

          {/* Section 5 */}
          <Section number="5" title="Google Analytics and Third-Party Services">
            <p className="text-study-black">
              We use tools like Google Analytics for website optimization. Google Analytics may
              collect anonymized data on how users interact with our website. For details, you
              can visit Google's Privacy Policies.
            </p>
          </Section>

          {/* Section 6 */}
          <Section number="6" title="Security and Data Retention">
            <p className="text-study-black mb-4">
              We take appropriate technical and organizational measures to protect your data.
              Despite our efforts, no data transmission or storage system can be guaranteed as
              entirely secure.
            </p>

            <p className="text-study-black">
              We retain data only as long as necessary to fulfill its purpose or as required by law.
            </p>
          </Section>

          {/* Section 7 */}
          <Section number="7" title="International Data Transfer">
            <p className="text-study-black">
              By using our services, you consent to the transfer of your information to servers
              and processing centers in jurisdictions with varying data protection laws.
            </p>
          </Section>

          {/* Section 8 */}
          <Section number="8" title="Your Rights">
            <p className="text-study-black mb-4">
              You have the following rights regarding your data:
            </p>

            <ul className="list-disc pl-6 mb-4 space-y-2 text-study-black">
              <li>Access your personal information.</li>
              <li>Request corrections to your data.</li>
              <li>Withdraw consent for processing where applicable.</li>
              <li>Delete your information unless it's legally required to retain.</li>
            </ul>

            <p className="text-study-black">
              To exercise these rights, contact us at{' '}
              <a
                href="mailto:privacy@studypassrdh.com"
                className="text-study-teal font-semibold hover:text-study-pink transition-all"
              >
                privacy@studypassrdh.com
              </a>
              .
            </p>
          </Section>

          {/* Section 9 */}
          <Section number="9" title="Updates to This Privacy Policy">
            <p className="text-study-black">
              We reserve the right to update this Policy. Substantial changes will be communicated
              by revising the date at the top of this Policy and/or providing notice on our website.
            </p>
          </Section>

          {/* Section 10 */}
          <Section number="10" title="Contact Information">
            <p className="text-study-black mb-4">
              If you have questions or concerns about this Policy, please contact us:
            </p>

            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-study-black font-semibold">Study Pass LLC</p>
              <p className="text-study-black">
                Email:{' '}
                <a
                  href="mailto:privacy@studypassrdh.com"
                  className="text-study-teal font-semibold hover:text-study-pink transition-all"
                >
                  privacy@studypassrdh.com
                </a>
              </p>
            </div>
          </Section>
        </div>

        {/* Back Link */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="text-study-teal font-bold hover:text-study-pink transition-all"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold text-study-black mb-4">
        {number}. {title}
      </h2>
      {children}
    </div>
  );
}

export default Privacy;
